import { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { DecisionCardUiOptions } from './schema';

export { DecisionCardSchema } from './schema';

interface OneOfEntry {
  const: boolean;
  title?: string;
}

interface PropertyEntry {
  title?: string;
  description?: string;
  type?: string;
  default?: boolean | string;
  oneOf?: OneOfEntry[];
}

/**
 * DecisionCard Field Component
 * Renders a styled card with a boolean radio group and dependent text inputs.
 * Text inputs are disabled when the boolean decision is false.
 * Use with type: object containing a boolean property and string properties.
 * @param props Props injected by Scaffolder for Field Extension Components
 * @returns DecisionCard React Element
 */
export const DecisionCard = (
  props: FieldExtensionComponentProps<
    Record<string, boolean | string>,
    DecisionCardUiOptions
  >,
) => {
  const {
    onChange,
    formData,
    uiSchema,
    disabled,
    readonly,
    schema: {
      title = 'Title',
      description = 'Description',
      properties = {},
      required: schemaRequired,
    },
    rawErrors,
  } = props;

  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';

  const [focusedKeys, setFocusedKeys] = useState<Record<string, boolean>>({});

  /** Identifies the boolean property key (the decision radio) */
  const decisionKey = useMemo(() => {
    return Object.keys(properties).find(
      key => (properties[key] as PropertyEntry)?.type === 'boolean',
    );
  }, [properties]);

  /** Identifies string property keys (dependent text fields) */
  const dependentKeys = useMemo(() => {
    return Object.keys(properties).filter(
      key => (properties[key] as PropertyEntry)?.type !== 'boolean',
    );
  }, [properties]);

  /** Derives radio labels from the boolean property's oneOf or defaults to Yes/No */
  const radioOptions = useMemo(() => {
    if (!decisionKey) return [];
    const property = (properties as Record<string, PropertyEntry>)[decisionKey];
    if (Array.isArray(property?.oneOf) && property.oneOf.length >= 2) {
      return property.oneOf.map(entry => ({
        value: entry.const,
        label: entry.title ?? (entry.const ? 'Yes' : 'No'),
      }));
    }
    return [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];
  }, [decisionKey, properties]);

  /** Current boolean value of the decision radio */
  const decisionValue = useMemo(() => {
    if (!decisionKey || !formData) return true;
    const val = formData[decisionKey];
    return typeof val === 'boolean' ? val : true;
  }, [decisionKey, formData]);

  /** Whether dependent text fields should be disabled */
  const isReadOnly = Boolean(disabled || readonly);
  const isDisabled = isReadOnly || !decisionValue;

  /**
   * Handles radio selection and emits the updated object
   * @param selectedValue The stringified boolean from the radio input
   */
  function handleRadioChange(selectedValue: string) {
    if (isReadOnly) {
      return;
    }

    const boolVal = selectedValue === 'true';
    const updated = {
      ...(formData ?? {}),
      ...(decisionKey ? { [decisionKey]: boolVal } : {}),
    };

    /** Clear dependent text fields when switching to false */
    if (!boolVal) {
      dependentKeys.forEach(depKey => {
        updated[depKey] = '';
      });
    }

    onChange(updated);
  }

  /**
   * Handles text field changes and emits the updated object
   * @param key The property key
   * @param value The new text value
   */
  function handleTextChange(key: string, value: string) {
    if (isReadOnly) {
      return;
    }

    onChange({
      ...(formData ?? {}),
      [key]: value,
    });
  }

  return (
    <Box sx={{ backgroundColor, padding: 2, borderRadius: 1 }}>
      <Box component="h2" sx={{ marginTop: 0, marginBottom: 1 }}>
        {title}
      </Box>
      <Box>{description}</Box>
      <Divider sx={{ marginBottom: 1, marginTop: 1 }} />

      {decisionKey && (
        <Box sx={{ marginTop: 2 }}>
          <FormControl component="fieldset" error={Boolean(rawErrors?.length)}>
            <FormLabel component="legend">
              {(properties as Record<string, PropertyEntry>)[decisionKey]
                ?.title ?? 'Select an option'}
            </FormLabel>
            <RadioGroup
              value={String(decisionValue)}
              onChange={event => handleRadioChange(event.target.value)}
            >
              {radioOptions.map(option => (
                <FormControlLabel
                  key={String(option.value)}
                  value={String(option.value)}
                  control={<Radio />}
                  disabled={isReadOnly}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      )}

      {dependentKeys.map(key => {
        const property = (properties as Record<string, PropertyEntry>)[key];
        const placeholder = uiSchema?.[key]?.['ui:placeholder'];
        const currentValue = formData?.[key] ?? '';

        const isRequired =
          Array.isArray(schemaRequired) && schemaRequired.includes(key);

        const helperText = !decisionValue
          ? 'This field is disabled because the option above is not selected.'
          : (property?.description ?? '');

        return (
          <Box key={key} sx={{ marginTop: 2 }}>
            <TextField
              fullWidth
              label={property?.title ?? key}
              placeholder={placeholder}
              InputLabelProps={{
                shrink: focusedKeys[key] || !!currentValue,
              }}
              required={isRequired && !isDisabled}
              value={currentValue}
              onChange={event => handleTextChange(key, event.target.value)}
              onFocus={() => setFocusedKeys(prev => ({ ...prev, [key]: true }))}
              onBlur={() => setFocusedKeys(prev => ({ ...prev, [key]: false }))}
              disabled={isDisabled}
              type="text"
              variant="outlined"
              helperText={helperText}
            />
          </Box>
        );
      })}
    </Box>
  );
};
